import { useState, useEffect } from 'react';
import axios from 'axios';
import { hostname } from '../utils';

interface UseDefaultFieldsResult {
  defaultFields: any;
  loading: boolean;
  error: string | null;
}

export const useDefaultFields = (listType: string): UseDefaultFieldsResult => {
  const [defaultFields, setDefaultFields] = useState<any>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!listType) {
      setDefaultFields(null);
      return;
    }

    const fetchDefaultFields = async () => {
      setLoading(true);
      setError(null);
      
      try {
        const response = await axios.get(`${hostname}/lists/default-fields/${listType}`);
        setDefaultFields(response.data.default_fields);
      } catch (err: any) {
        setError(err.response?.data?.error || 'Failed to fetch default fields');
      } finally {
        setLoading(false);
      }
    };

    fetchDefaultFields();
  }, [listType]);

  return { defaultFields, loading, error };
};

// Utility function to get available lead fields from default fields
export const getAvailableLeadFields = (defaultFields: any) => {
  if (!defaultFields) return [];

  const fields: any[] = [];
  
  // Add all fields (now including flattened company fields)
  Object.keys(defaultFields).forEach(key => {
    const field = defaultFields[key];
    // Check if this is new field format with predefined label
    if (typeof field === 'object' && field.label) {
      fields.push({
        key,
        label: field.label,
        type: field.datatype || field.type
      });
    } else if (typeof field === 'object' && field.name) {
      // Handle nested company fields (backward compatibility)
      Object.keys(field).forEach(nestedKey => {
        const nestedField = field[nestedKey];
        if (typeof nestedField === 'object' && nestedField.label) {
          fields.push({
            key: `${key}.${nestedKey}`,
            label: nestedField.label,
            type: nestedField.datatype || nestedField.type
          });
        } else {
          // Backward compatibility: use camel case conversion
          fields.push({
            key: `${key}.${nestedKey}`,
            label: `${key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())} ${nestedKey.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}`,
            type: nestedField
          });
        }
      });
    } else {
      // Backward compatibility: use camel case conversion
      fields.push({
        key,
        label: key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase()),
        type: field
      });
    }
  });
  
  return fields;
};